// API base URL - should be configured based on environment
const API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL || 'https://obta-api.trade.workers.dev'}/api`;

// API token from environment variable - NEVER use hard-coded tokens in production
const API_TOKEN = import.meta.env.VITE_API_TOKEN;

// Validate API token is configured
if (!API_TOKEN) {
  console.error('VITE_API_TOKEN environment variable is not set');
  throw new Error('API token not configured. Please set VITE_API_TOKEN in your environment.');
}

export class ApiService {
  private baseUrl: string;
  private apiToken: string;

  constructor(baseUrl?: string, apiToken?: string) {
    this.baseUrl = baseUrl || API_BASE_URL;
    this.apiToken = apiToken || API_TOKEN;
  }

  /**
   * Get JWT token from localStorage
   */
  private getJwtToken(): string | null {
    try {
      return localStorage.getItem('auth_token');
    } catch (error) {
      console.error('Failed to get JWT token:', error);
      return null;
    }
  }

  /**
   * Make an authenticated API request
   * Uses JWT token if available, falls back to API token
   */
  async makeRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const url = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}${endpoint}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as Record<string, string>,
    };

    // Try to use JWT token first (for user-based requests)
    const jwtToken = this.getJwtToken();
    if (jwtToken) {
      headers['Authorization'] = `Bearer ${jwtToken}`;
    } else {
      // Fall back to API token for system-based requests
      headers['X-API-Token'] = this.apiToken;
    }

    const requestOptions: RequestInit = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, requestOptions);

      // Handle 401 responses by clearing invalid JWT tokens
      if (response.status === 401 && jwtToken) {
        console.warn('JWT token appears to be invalid, clearing auth state');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        // Could trigger a logout event here if needed
      }

      return response;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  /**
   * Make a GET request
   */
  async get(endpoint: string): Promise<Response> {
    return this.makeRequest(endpoint, { method: 'GET' });
  }

  /**
   * Make a POST request
   */
  async post(endpoint: string, data?: any): Promise<Response> {
    return this.makeRequest(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * Make a PATCH request
   */
  async patch(endpoint: string, data?: any): Promise<Response> {
    return this.makeRequest(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * Make a DELETE request
   */
  async delete(endpoint: string): Promise<Response> {
    return this.makeRequest(endpoint, { method: 'DELETE' });
  }
}

// Export a singleton instance
export const apiService = new ApiService();
